import { defineStore } from 'pinia';
import axios from 'axios';

// Homepage builder (CMS) admin store. Mirrors useBlogStore.js conventions.
export const useHomepageStore = defineStore('homepageSections', {
    state: () => ({
        sections: [],
        loading: false,
        saving: false,
    }),

    actions: {
        async fetchSections() {
            this.loading = true;
            try {
                const { data } = await axios.get('/admin/homepage-sections');
                this.sections = (data.data ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
            } finally {
                this.loading = false;
            }
        },

        async createSection(payload) {
            this.saving = true;
            try {
                const { data } = await axios.post('/admin/homepage-sections', payload);
                this.sections.push(data.data);
                this.sections.sort((a, b) => a.sort_order - b.sort_order);
                return data.data;
            } finally {
                this.saving = false;
            }
        },

        // Recreates any default section missing from the table (by key) without
        // touching sections that already exist — safe recovery action, not a reset.
        async restoreDefaults() {
            this.loading = true;
            try {
                const { data } = await axios.post('/admin/homepage-sections/restore-defaults');
                this.sections = (data.data ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
                return this.sections;
            } finally {
                this.loading = false;
            }
        },

        // `payload` may be a plain object or a FormData (when a file is attached).
        async updateSection(id, payload) {
            this.saving = true;
            try {
                const { data } = await axios.post(`/admin/homepage-sections/${id}`, payload);
                this.replaceLocal(data.data);
                return data.data;
            } finally {
                this.saving = false;
            }
        },

        async reorder(orderedIds) {
            await axios.patch('/admin/homepage-sections/reorder', { order: orderedIds });
            this.sections = orderedIds
                .map((id, index) => {
                    const section = this.sections.find((s) => s.id === id);
                    return section ? { ...section, sort_order: index } : null;
                })
                .filter(Boolean);
        },

        async toggleActive(id) {
            const { data } = await axios.patch(`/admin/homepage-sections/${id}/toggle-active`);
            this.replaceLocal(data.data);
            return data.data;
        },

        replaceLocal(section) {
            const idx = this.sections.findIndex((s) => s.id === section.id);
            if (idx !== -1) this.sections[idx] = section;
        },
    },
});
